import {useState, useEffect, useCallback} from 'react';
import {useNavigate} from 'react-router';
import dayjs from 'dayjs';

import * as MedicalRecordApi from '../api/MedicalRecordApi';
import * as AccountApi from '../api/AccountApi';
import PageShell from '../components/PageShell';
import medicalRecordsImage from '../assets/image/icon-records.png';
import DateRangePicker from '../components/DateRangePicker';

import {
  FilterBar,
  AddButton,
  SegmentedControl,
  EmptyState,
  LoadingRows,
  ConfirmSheet,
} from '../components/dental-records/MedicalRecordUI';
import MedicalRecordRow from '../components/dental-records/MedicalRecordRow';

const TOKENS = {primary: '#c43b71', ink: '#1A1A2E', inkMuted: '#6B7280'};
const FONT = {display: 'var(--font-display), Outfit, system-ui'};

export default function MedicalRecords() {
  const navigate = useNavigate();
  const [medicalRecords, setMedicalRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sequence, setSequence] = useState('descending');
  const [dateRange, setDateRange] = useState([
    dayjs().subtract('1', 'month').format('YYYY-MM-DD'),
    dayjs().add('1', 'month').format('YYYY-MM-DD'),
  ]);
  const [storageUsage, setStorageUsage] = useState({used: 0, total: 15 * 1024 * 1024 * 1024});
  const [expandedId, setExpandedId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const formatBytes = (bytes, decimals = 1) => {
    if (!+bytes) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  };

  const handleChangeDateRange = (date) => {
    if (!date) {
      setDateRange([
        dayjs().subtract('10', 'year').format('YYYY-MM-DD'),
        dayjs().add('10', 'year').format('YYYY-MM-DD'),
      ]);
      return;
    }
    setDateRange([dayjs(date[0]).format('YYYY-MM-DD'), dayjs(date[1]).format('YYYY-MM-DD')]);
  };

  const fetchRecords = useCallback(() => {
    setLoading(true);
    MedicalRecordApi.getAllMedicalRecord(sequence, dateRange)
      .then((records) => {
        if (Array.isArray(records)) {
          setMedicalRecords(records);
        } else {
          setMedicalRecords([]);
        }
      })
      .catch((err) => console.error('Failed to fetch medical records:', err))
      .finally(() => setLoading(false));
  }, [dateRange, sequence]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  useEffect(() => {
    AccountApi.getStorageUsage()
      .then((data) => {
        if (data && data.total_storage) {
          setStorageUsage({
            used: data.used_storage,
            total: data.total_storage,
          });
        }
      })
      .catch((err) => console.error('Failed to fetch storage usage:', err));
  }, []);

  const handleDelete = () => {
    if (!deleteId) return;
    setIsDeleting(true);
    MedicalRecordApi.deleteMedicalRecord(deleteId)
      .then(() => {
        setMedicalRecords((prev) => prev.filter((r) => r.id !== deleteId));
        setDeleteId(null);
        setExpandedId(null);
      })
      .catch((err) => console.error('Failed to delete:', err))
      .finally(() => setIsDeleting(false));
  };

  const hasRangeFilter = dateRange[0] !== dayjs().subtract('10', 'year').format('YYYY-MM-DD');

  return (
    <PageShell title="Dental Records" titleIcon={medicalRecordsImage} showBackButton={true}>
      {/* Date picker override for custom styling */}
      <style>{`
        .react-daterange-picker__wrapper { border: none !important; }
        .react-daterange-picker__inputGroup__input { font-family: var(--font-body) !important; color: ${TOKENS.ink} !important; font-weight: 600; }
        .react-daterange-picker { background: rgba(255,255,255,0.85); backdrop-filter: blur(20px); border-radius: 999px; padding: 4px 12px; height: 44px; display: inline-flex; align-items: center; border: 1px solid #E5E7EB; box-shadow: 0 2px 8px rgba(0,0,0,0.04); }
      `}</style>

      <FilterBar
        range={<DateRangePicker onChange={handleChangeDateRange} value={dateRange} clearIcon={null} />}
        tabs={
          <SegmentedControl
            label="Sort order"
            options={[
              {value: 'descending', label: 'Newest first'},
              {value: 'ascending', label: 'Oldest first'},
            ]}
            value={sequence}
            onChange={setSequence}
          />
        }
        action={<AddButton label="Add new" href="/dental-records/add" />}
        style={{marginBottom: 24}}
      />

      {loading ? (
        <LoadingRows rows={3} height={64} />
      ) : medicalRecords.length > 0 ? (
        <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
          {medicalRecords.map((record) => (
            <MedicalRecordRow
              key={record.id}
              record={record}
              expanded={expandedId === record.id}
              onToggle={() => setExpandedId(expandedId === record.id ? null : record.id)}
              onDelete={() => setDeleteId(record.id)}
              onEdit={() => navigate(`/dental-records/${record.id}`)}
            />
          ))}
        </div>
      ) : hasRangeFilter ? (
        <EmptyState
          title="No records found"
          body="Try a wider date range, or clear the dates to see everything."
          action={{label: 'Show all dates', onClick: () => handleChangeDateRange(null)}}
        />
      ) : (
        <EmptyState
          icon={
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 2c-5.5 0-10 4.5-10 10s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2m0 2c4.4 0 8 3.6 8 8s-3.6 8-8 8-8-3.6-8-8 3.6-8 8-8m-1 4v3l2.2.9m0 0l2.5 1.5m-5.2-.4v4.5m-2-2.5h1.5"
                stroke={TOKENS.primary}
                strokeWidth="1.2"
                fill="none"
              />
            </svg>
          }
          title="No records yet"
          body="Add your X-rays, treatment notes, and receipts here."
          action={{label: 'Add your first record', href: '/dental-records/add'}}
          primary
        />
      )}

      {/* Storage usage display matching the old bar but stylized */}
      <div
        style={{
          marginTop: 32,
          padding: 20,
          background: 'rgba(255,255,255,0.85)',
          borderRadius: 16,
          border: '1px solid #E5E7EB',
        }}
      >
        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 8}}>
          <span style={{fontFamily: FONT.display, fontSize: 14, fontWeight: 700, color: TOKENS.ink}}>
            Storage Usage
          </span>
          <span style={{fontFamily: 'var(--font-body)', fontSize: 13, color: TOKENS.inkMuted}}>
            {formatBytes(storageUsage.used)} of {formatBytes(storageUsage.total)} used
          </span>
        </div>
        <div
          style={{
            width: '100%',
            height: 8,
            borderRadius: 999,
            background: 'rgba(26,26,46,0.08)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${Math.min(100, (storageUsage.used / storageUsage.total) * 100)}%`,
              height: '100%',
              background: TOKENS.primary,
              borderRadius: 999,
            }}
          ></div>
        </div>
      </div>

      <ConfirmSheet
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        title="Delete this record?"
        body="This removes the record and its attachments from your storage."
        confirmLabel="Delete"
        onConfirm={handleDelete}
        destructive
        busy={isDeleting}
      />
    </PageShell>
  );
}
